import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Building2, Save, Upload, Loader2, Image as ImageIcon } from 'lucide-react';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

function CompanyProfileForm() {
  var loadingState = React.useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var savingState = React.useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];
  
  var uploadingState = React.useState(false);
  var uploading = uploadingState[0];
  var setUploading = uploadingState[1];
  
  var formState = React.useState({
    company_name: '',
    address: '',
    state: '',
    district: '',
    pin_code: '',
    mobile: '',
    email: '',
    gst_number: '',
    pan_number: '',
    logo_url: '',
    bank_name: '',
    bank_account_number: '',
    bank_ifsc: ''
  });
  var form = formState[0];
  var setForm = formState[1];

  React.useEffect(function() {
    fetchProfile();
  }, []);

  function fetchProfile() {
    setLoading(true);
    var token = localStorage.getItem('token');
    axios.get(API + '/admin/company-profile', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      if (response.data) {
        setForm({
          company_name: response.data.company_name || '',
          address: response.data.address || '',
          state: response.data.state || '',
          district: response.data.district || '',
          pin_code: response.data.pin_code || '',
          mobile: response.data.mobile || '',
          email: response.data.email || '',
          gst_number: response.data.gst_number || '',
          pan_number: response.data.pan_number || '',
          logo_url: response.data.logo_url || '',
          bank_name: response.data.bank_name || '',
          bank_account_number: response.data.bank_account_number || '',
          bank_ifsc: response.data.bank_ifsc || ''
        });
      }
    }).catch(function() {
      // Profile load failed — form will show empty fields.
    }).finally(function() {
      setLoading(false);
    });
  }

  function handleChange(field, value) {
    setForm(function(prev) {
      var updated = Object.assign({}, prev);
      updated[field] = value;
      return updated;
    });
  }

  function handleLogoUpload(event) {
    var file = event.target.files[0];
    if (!file) return;
    
    var allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF or WEBP');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    setUploading(true);
    var token = localStorage.getItem('token');
    var formData = new FormData();
    formData.append('file', file);
    
    axios.post(API + '/admin/upload-company-logo', formData, {
      headers: {
        Authorization: 'Bearer ' + token,
        'Content-Type': 'multipart/form-data'
      }
    }).then(function(response) {
      var logoUrl = process.env.REACT_APP_BACKEND_URL + response.data.url;
      handleChange('logo_url', logoUrl);
      toast.success('Logo uploaded successfully');
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    }).finally(function() {
      setUploading(false);
    });
  }

  function saveProfile() {
    if (!form.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }
    
    setSaving(true);
    var token = localStorage.getItem('token');
    
    axios.post(API + '/admin/company-profile', form, {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      toast.success('Company profile saved successfully');
    }).catch(function(err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    }).finally(function() {
      setSaving(false);
    });
  }

  if (loading) {
    return React.createElement('div', { className: 'flex items-center justify-center py-8' },
      React.createElement(Loader2, { className: 'w-6 h-6 animate-spin text-blue-600 mr-2' }),
      React.createElement('span', { className: 'text-gray-600' }, 'Loading company profile...')
    );
  }

  return React.createElement(Card, { 'data-testid': 'company-profile-form' },
    React.createElement(CardHeader, null,
      React.createElement(CardTitle, { className: 'flex items-center gap-2' },
        React.createElement(Building2, { className: 'w-5 h-5 text-blue-600' }),
        'Company Profile'
      ),
      React.createElement(CardDescription, null,
        'Configure your company details. These will appear on all invoices as seller information.'
      )
    ),
    React.createElement(CardContent, { className: 'space-y-6' },
      // Logo Upload Section
      React.createElement('div', { className: 'space-y-3' },
        React.createElement(Label, { className: 'text-sm font-medium' }, 'Company Logo'),
        React.createElement('div', { className: 'flex items-start gap-4' },
          form.logo_url
            ? React.createElement('div', { className: 'relative' },
                React.createElement('img', {
                  src: form.logo_url,
                  alt: 'Company Logo',
                  className: 'w-32 h-32 object-contain border rounded-lg bg-gray-50'
                }),
                React.createElement(Button, {
                  variant: 'ghost',
                  size: 'sm',
                  className: 'absolute -top-2 -right-2 h-6 w-6 p-0 bg-red-100 hover:bg-red-200 rounded-full',
                  onClick: function() { handleChange('logo_url', ''); }
                }, '×')
              )
            : React.createElement('div', { className: 'w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center bg-gray-50' },
                React.createElement(ImageIcon, { className: 'w-8 h-8 text-gray-400' })
              ),
          React.createElement('div', { className: 'flex-1' },
            React.createElement('input', {
              type: 'file',
              id: 'logo-upload-input',
              accept: 'image/*',
              onChange: handleLogoUpload,
              className: 'hidden'
            }),
            React.createElement(Button, {
              type: 'button',
              variant: 'outline',
              disabled: uploading,
              className: 'gap-2',
              onClick: function() { 
                document.getElementById('logo-upload-input').click(); 
              }
            },
              uploading
                ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' })
                : React.createElement(Upload, { className: 'w-4 h-4' }),
              uploading ? 'Uploading...' : 'Upload Logo'
            ),
            React.createElement('p', { className: 'text-xs text-gray-500 mt-2' }, 'Max 5MB. Supported: JPEG, PNG, GIF, WEBP')
          )
        )
      ),
      
      // Company Name
      React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-4' },
        React.createElement('div', { className: 'md:col-span-2' },
          React.createElement(Label, { htmlFor: 'company_name' }, 'Company Name *'),
          React.createElement(Input, {
            id: 'company_name',
            value: form.company_name,
            onChange: function(e) { handleChange('company_name', e.target.value); },
            placeholder: 'Your Company Name',
            className: 'mt-1'
          })
        ),
        
        // Address
        React.createElement('div', { className: 'md:col-span-2' },
          React.createElement(Label, { htmlFor: 'address' }, 'Address'),
          React.createElement(Textarea, {
            id: 'address',
            value: form.address,
            onChange: function(e) { handleChange('address', e.target.value); },
            placeholder: 'Street address, building name, etc.',
            rows: 2,
            className: 'mt-1'
          })
        ),
        
        // State, District, Pin
        React.createElement('div', null,
          React.createElement(Label, { htmlFor: 'state' }, 'State'),
          React.createElement(Input, {
            id: 'state',
            value: form.state,
            onChange: function(e) { handleChange('state', e.target.value); },
            placeholder: 'e.g., Maharashtra',
            className: 'mt-1'
          })
        ),
        React.createElement('div', null,
          React.createElement(Label, { htmlFor: 'district' }, 'District'),
          React.createElement(Input, {
            id: 'district',
            value: form.district,
            onChange: function(e) { handleChange('district', e.target.value); },
            placeholder: 'e.g., Mumbai',
            className: 'mt-1'
          })
        ),
        React.createElement('div', null,
          React.createElement(Label, { htmlFor: 'pin_code' }, 'PIN Code'),
          React.createElement(Input, {
            id: 'pin_code',
            value: form.pin_code,
            onChange: function(e) { handleChange('pin_code', e.target.value); },
            placeholder: 'e.g., 400001',
            className: 'mt-1'
          })
        ),
        
        // Contact
        React.createElement('div', null,
          React.createElement(Label, { htmlFor: 'mobile' }, 'Mobile'),
          React.createElement(Input, {
            id: 'mobile',
            value: form.mobile,
            onChange: function(e) { handleChange('mobile', e.target.value); },
            placeholder: 'e.g., +91 9876543210',
            className: 'mt-1'
          })
        ),
        React.createElement('div', null,
          React.createElement(Label, { htmlFor: 'email' }, 'Email'),
          React.createElement(Input, {
            id: 'email',
            type: 'email',
            value: form.email,
            onChange: function(e) { handleChange('email', e.target.value); },
            placeholder: 'e.g., contact@company.com',
            className: 'mt-1'
          })
        )
      ),
      
      // Tax Details
      React.createElement('div', { className: 'border-t pt-4' },
        React.createElement('h4', { className: 'text-sm font-semibold text-gray-700 mb-3' }, 'Tax & Legal Details'),
        React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-4' },
          React.createElement('div', null,
            React.createElement(Label, { htmlFor: 'gst_number' }, 'GST Number (GSTIN)'),
            React.createElement(Input, {
              id: 'gst_number',
              value: form.gst_number,
              onChange: function(e) { handleChange('gst_number', e.target.value.toUpperCase()); },
              placeholder: 'e.g., 27AABCU9603R1ZM',
              className: 'mt-1 uppercase'
            })
          ),
          React.createElement('div', null,
            React.createElement(Label, { htmlFor: 'pan_number' }, 'PAN Number'),
            React.createElement(Input, {
              id: 'pan_number',
              value: form.pan_number,
              onChange: function(e) { handleChange('pan_number', e.target.value.toUpperCase()); },
              placeholder: 'e.g., AABCU9603R',
              className: 'mt-1 uppercase'
            })
          )
        )
      ),
      
      // Bank Details
      React.createElement('div', { className: 'border-t pt-4' },
        React.createElement('h4', { className: 'text-sm font-semibold text-gray-700 mb-3' }, 'Bank Details (for invoice footer)'),
        React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-3 gap-4' },
          React.createElement('div', null,
            React.createElement(Label, { htmlFor: 'bank_name' }, 'Bank Name'),
            React.createElement(Input, {
              id: 'bank_name',
              value: form.bank_name,
              onChange: function(e) { handleChange('bank_name', e.target.value); },
              placeholder: 'e.g., HDFC Bank',
              className: 'mt-1'
            })
          ),
          React.createElement('div', null,
            React.createElement(Label, { htmlFor: 'bank_account_number' }, 'Account Number'),
            React.createElement(Input, {
              id: 'bank_account_number',
              value: form.bank_account_number,
              onChange: function(e) { handleChange('bank_account_number', e.target.value); },
              placeholder: 'e.g., 12345678901234',
              className: 'mt-1'
            })
          ),
          React.createElement('div', null,
            React.createElement(Label, { htmlFor: 'bank_ifsc' }, 'IFSC Code'),
            React.createElement(Input, {
              id: 'bank_ifsc',
              value: form.bank_ifsc,
              onChange: function(e) { handleChange('bank_ifsc', e.target.value.toUpperCase()); },
              placeholder: 'e.g., HDFC0001234',
              className: 'mt-1 uppercase'
            })
          )
        )
      ),
      
      // Save Button
      React.createElement('div', { className: 'flex justify-end pt-4 border-t' },
        React.createElement(Button, {
          onClick: saveProfile,
          disabled: saving,
          className: 'gap-2'
        },
          saving
            ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' })
            : React.createElement(Save, { className: 'w-4 h-4' }),
          saving ? 'Saving...' : 'Save Company Profile'
        )
      ),
      
      // Info Note
      React.createElement('div', { className: 'p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800' },
        React.createElement('strong', null, 'Note: '),
        'These details will be automatically added to all new invoices as "Seller Details".'
      )
    )
  );
}

export default CompanyProfileForm;
